import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';

/**
 * Fonction utilitaire pour transformer les timestamps Firestore en Date
 */
const transformFirestoreTimestamp = (val: any): Date => {
  if (val instanceof Timestamp) {
    return val.toDate();
  }
  if (val instanceof Date) {
    return val;
  }
  if (typeof val === 'string') {
    return new Date(val);
  }
  if (val && typeof val === 'object' && val.seconds !== undefined) {
    // Timestamp Firestore sous forme d'objet
    return new Date(val.seconds * 1000);
  }
  throw new Error(`Cannot transform timestamp: ${val}`);
};

/**
 * Schéma Zod pour les timestamps Firestore
 */
const firestoreTimestampSchema = z.any().transform(transformFirestoreTimestamp);

/**
 * Interface pour les permissions d'un rôle
 */
export interface RolePermissions {
  canViewOrders: boolean;
  canManageOrders: boolean;
  canManageConfiguration: boolean;
  canViewAnalytics: boolean;
  canManageUsers: boolean;
}

/**
 * Schéma Zod pour les permissions
 */
export const RolePermissionsSchema = z.object({
  canViewOrders: z.boolean().default(false),
  canManageOrders: z.boolean().default(false),
  canManageConfiguration: z.boolean().default(false),
  canViewAnalytics: z.boolean().default(false),
  canManageUsers: z.boolean().default(false),
});

/**
 * Schéma de validation pour un rôle
 */
export const RoleSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Le nom du rôle est requis'),
  displayName: z.string().min(1, 'Le nom d\'affichage est requis'),
  description: z.string().optional(),
  permissions: RolePermissionsSchema,
  isSystem: z.boolean().default(false), // Indique si c'est un rôle système (non modifiable)
  createdAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema,
});

/**
 * Interface TypeScript pour un rôle
 */
export interface Role {
  id: string;
  name: string; // Identifiant unique du rôle (ex: 'owner', 'admin', 'member', 'viewer')
  displayName: string; // Nom d'affichage (ex: 'Propriétaire', 'Administrateur')
  description?: string; // Description du rôle
  permissions: RolePermissions;
  isSystem: boolean; // Indique si c'est un rôle système (non modifiable)
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Interface pour créer un nouveau rôle
 */
export interface CreateRoleRequest {
  name: string;
  displayName: string;
  description?: string;
  permissions: RolePermissions;
  isSystem?: boolean;
}

/**
 * Interface pour mettre à jour un rôle
 */
export interface UpdateRoleRequest {
  displayName?: string;
  description?: string;
  permissions?: Partial<RolePermissions>;
}

/**
 * Permissions vides par défaut (utilisées uniquement en fallback si le rôle n'existe pas)
 */
export const EMPTY_PERMISSIONS: RolePermissions = {
  canViewOrders: false,
  canManageOrders: false,
  canManageConfiguration: false,
  canViewAnalytics: false,
  canManageUsers: false,
};

